// SAB-401: NFL mobile players list — mirror of
// mlb-beta/MLBBetaPlayersMobile.tsx structure, adapted for NFL.
//
// Architecture decision (Adam, locked June 15): mirror inline queries, do
// NOT extract a shared useFantasyPlayers hook. MLB beta has 44 paid live
// users; zero regression risk on their code path. This file talks to the
// existing get_nfl_player_leaderboard RPC (same one the desktop
// NFLPlayersPage uses) — no backend changes, no shared data layer.
//
// Layout: same band stack as MLB —
//   Band 0: AcquisitionStrip (FAAB pill, if non-FCFS)
//   Band 1: sticky search + filter icon
//   Band 2: sticky position priority row (Seg + dropdown)
//   Band 3: active filter chips (conditional)
//   Band 4: result count + sort menu
//   Band 5: virtualized list of PlayerCardMobile
//
// Differences from MLB:
//   - No probable-pitcher / 2-start-pitcher logic (MLB-only)
//   - No PlayerType (Hitters/Pitchers) Seg — NFL's position chip IS the
//     offense/defense split
//   - No time-range filter (NFL RPC is season-only today)
//   - Stat columns aren't dynamic — they're a fixed Trash/Anti/Avg/GP
//     strip plus optional Projections row
//   - DEF rendering is NOVEL — Shield placeholder, polish deferred
//
// Diff against MLB's data load:
//   - MLB pulls the full player set (1k pages) then filters/sorts client-side
//   - NFL hits the RPC with server-side filter/sort/limit — much smaller
//     response, but means the virtualizer only renders one page at a time
//   - "Load more" pagination expands the in-memory list; virtualizer
//     handles the resulting grow. Mirrors NFLPlayersPage's PAGE_SIZE = 50
//
// Projections come from a supplementary `.from('nfl_players').select(
//   'id, projected_*, adp, status').in('id', [...])` after the RPC returns — the
// RPC doesn't expose these columns and we're not modifying the RPC.
// SAB-749: `status` rides along on that same fetch so an IR/PUP player is marked
// at the point of decision rather than one screen away.

import { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import * as Sentry from '@sentry/react';
import { useWindowVirtualizer } from '@tanstack/react-virtual';
import { supabase } from '@/integrations/supabase/client';
import { useNFLLeague } from '@/contexts/NFLLeagueContext';
import { canAcquirePlayers, PRE_DRAFT_NOTICE } from '@/lib/nflDraftGate';
import { classifyAcquireResponse } from '@/lib/nflAcquireOutcome';
import NFLWaiverResults from './NFLWaiverResults';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { SlidersHorizontal, UserSearch } from 'lucide-react';
import { cn } from '@/lib/utils';
import { PlayerAvailabilityBadge } from './PlayerAvailabilityBadge';
import { useToast } from '@/hooks/use-toast';
import NFLPlayerDetailModal from './NFLPlayerDetailModal';
import { AcquisitionStrip } from './players-mobile/AcquisitionStrip';
import {
  FilterSheet, type OwnershipFilter,
} from './players-mobile/FilterSheet';
import { PositionPriorityRow, NFL_POSITION_FILTERS } from './players-mobile/PositionPriorityRow';
import { useLeagueRosterability } from '@/hooks/useStartablePositions';
import NFLAddDropOverlay, { type AddDropTarget } from './NFLAddDropOverlay';
import { scopeChips, FLEX_CHIP, IDP_CHIP_GROUPS } from '@/lib/nflRosterability';
import { ActiveFilterChips } from './players-mobile/ActiveFilterChips';
import { SortMenu, type MobileSortKey, type SortDir } from './players-mobile/SortMenu';
import { PlayerCardMobile, type NFLPlayerCardData } from './players-mobile/PlayerCardMobile';

const FILTER_STORAGE_KEY = 'nfl-players-mobile-filters';
const FILTER_VERSION = 1;
const PAGE_SIZE = 50;

interface RpcPlayerRow {
  // SAB-1176: the six stat keys, aggregated server-side (migration 20260914200000).
  // Postgres numerics arrive as strings over JSON, hence number | string.
  total_yds?: number | string;
  total_td?: number | string;
  total_int?: number | string;
  total_fum?: number | string;
  total_drp?: number | string;
  total_tfl?: number | string;
  id: string;
  full_name: string;
  position: string;
  nfl_franchise_id: string | null;
  nfl_team_abbr: string | null;
  headshot_url: string | null;
  status: string;
  injury_status: string | null;
  games_played: number;
  total_sabotage: number;
  total_trash: number;
  total_anti: number;
  avg_sabotage: number;
  // SAB-811: the shipped board, straight from the RPC. Nullable on purpose — IDP carry no
  // projection for year one and 86 board players have no basis. Never coalesce to 0.
  proj_sab_pts: number | null;
  adp: number | null;
  proj_source: string | null;
  owner_team_name: string | null;
  // SAB-1045: server-derived waiver designation, carried onto the card by the ...r spread
  // below. Declared here so the type says what the runtime already does.
  waiver_state: 'post_draft' | 'dropped' | null;
  waiver_clears_at: string | null;
  // SAB-1033: relevance, not existence — see NFLPlayersPage. Carried onto the card by the
  // ...r spread so the marker renders identically on both surfaces.
  active_2026: boolean | null;
}

interface ProjectionRow {
  id: string;
  projected_trash_points: number | null;
  projected_anti_points: number | null;
  projected_total_points: number | null;
  adp: number | null;
  // SAB-749: roster availability, display only — never reorders or filters.
  status: string | null;
}

const saveFilters = (filters: Record<string, unknown>) => {
  try {
    sessionStorage.setItem(
      FILTER_STORAGE_KEY,
      JSON.stringify({ ...filters, _v: FILTER_VERSION }),
    );
  } catch { /* sessionStorage unavailable */ }
};

interface NFLPlayersMobileProps {
  /**
   * SAB-1062: opens the propose-trade flow pre-targeted at a team, optionally with a player
   * pre-added to their side. Threaded from NFLStaging so every surface that can open the
   * player-detail modal can also act on its Trade button -- a surface that cannot supply
   * this simply does not render the button, rather than rendering a dead one.
   */
  onTradeWith?: (teamId: string, playerId?: string) => void;
}

export default function NFLPlayersMobile({ onTradeWith }: NFLPlayersMobileProps = {}) {
  const { currentLeague, currentTeam } = useNFLLeague();

  /**
   * SAB-1016: mobile had NO drop selection. handleAdd fired straight at the server, the
   * server returned 409 NO_VALID_ROSTER_ARRANGEMENT on a full roster, and the user got a red
   * toast telling them to drop someone — with no way to do it from this screen. The overlay
   * is that way.
   */
  const [addDropTarget, setAddDropTarget] = useState<AddDropTarget | null>(null);
  // SAB-1017: mobile learns the player is on waivers from the SERVER's typed refusal rather
  // than pre-checking, so there is one authority on waiver state and no second opinion here
  // to drift from it.
  const [overlayMode, setOverlayMode] = useState<'add' | 'bid'>('add');
  // SAB-1038: the results log belongs on BOTH surfaces. Mounting it desktop-only would put
  // a league-transparency page out of reach of most of the league — caught by measuring at
  // 390 rather than by reading the JSX.
  const [showResults, setShowResults] = useState(false);

  // SAB-995 / SAB-998: same derivation as the desktop page and the draft room. Scoped on
  // the option OBJECTS rather than on bare values so the labels travel with them.
  const { startable, hasFlex, flex } = useLeagueRosterability(currentLeague?.settings?.roster_slots);
  const positionOptions = useMemo(() => {
    const values = scopeChips(
      NFL_POSITION_FILTERS.map(o => o.value),
      startable,
      { alwaysKeep: ['All'], groupOf: (c) => IDP_CHIP_GROUPS[c] ?? null },
    );
    const scoped = NFL_POSITION_FILTERS.filter(o => values.includes(o.value));
    // See the desktop page: gated on `flex` having loaded, not merely on hasFlex.
    return hasFlex && flex !== null
      ? [...scoped, { value: FLEX_CHIP, label: 'FLEX' }]
      : scoped;
  }, [startable, hasFlex, flex]);
  const { toast } = useToast();

  // Hydrate persisted filters
  const stored = useMemo(() => {
    try {
      const raw = JSON.parse(sessionStorage.getItem(FILTER_STORAGE_KEY) || '{}');
      if (raw._v !== FILTER_VERSION) return {} as Record<string, unknown>;
      return raw as Record<string, unknown>;
    } catch { return {} as Record<string, unknown>; }
  }, []);

  const [search, setSearch] = useState<string>((stored.search as string) || '');
  const [debouncedSearch, setDebouncedSearch] = useState(search);
  const [posFilter, setPosFilter] = useState<string>((stored.posFilter as string) || 'All');
  const [ownershipFilter, setOwnershipFilter] = useState<OwnershipFilter>(
    (stored.ownershipFilter as OwnershipFilter) || 'available',
  );
  const [sortKey, setSortKey] = useState<MobileSortKey>((stored.sortKey as MobileSortKey) || 'total_sabotage');
  const [sortDir, setSortDir] = useState<SortDir>((stored.sortDir as SortDir) || 'desc');

  useEffect(() => {
    saveFilters({ search, posFilter, ownershipFilter, sortKey, sortDir });
  }, [search, posFilter, ownershipFilter, sortKey, sortDir]);

  // Debounce search → triggers RPC refetch
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search.trim()), 300);
    return () => clearTimeout(t);
  }, [search]);

  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());
  const [filterSheetOpen, setFilterSheetOpen] = useState(false);
  const [detailPlayerId, setDetailPlayerId] = useState<string | null>(null);

  // ─── Player data via RPC + supplementary projections fetch ─────────────
  const [players, setPlayers] = useState<NFLPlayerCardData[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [loadAttempt, setLoadAttempt] = useState(0);

  // Reset list whenever the query shape changes
  useEffect(() => { setPage(0); }, [posFilter, debouncedSearch, sortKey, sortDir, ownershipFilter]);

  const fetchPlayers = useCallback(async (pageIndex: number, append: boolean) => {
    if (!currentLeague) {
      setLoading(false);
      return;
    }
    if (append) setLoadingMore(true);
    else { setLoading(true); setError(null); }

    try {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any -- RPC return type is JSON; we type the inner rows explicitly below
      const { data, error: rpcErr } = await supabase.rpc('get_nfl_player_leaderboard' as any, {
        p_league_id: currentLeague.id,
        // SAB-998: FLEX is a slot, not a position -- see the desktop page. Sent as the
        // eligible SET so the server does the filtering; a client-side pass would run over
        // the page the server already chose and drop the rest silently.
        p_position: posFilter === 'All' || posFilter === FLEX_CHIP ? null : posFilter,
        p_positions: posFilter === FLEX_CHIP && flex ? [...flex] : null,
        p_search: debouncedSearch || null,
        p_sort_key: sortKey,
        p_sort_dir: sortDir,
        p_limit: PAGE_SIZE,
        p_offset: pageIndex * PAGE_SIZE,
        // SAB-993: server-side now. See the note where the client-side filter used to be.
        p_availability: ownershipFilter === 'all' ? null : ownershipFilter,
      });
      if (rpcErr) throw rpcErr;
      const result = data as { total?: number; players?: RpcPlayerRow[] };
      const raw = (result?.players ?? []) as RpcPlayerRow[];

      // Supplementary projection fetch — RPC doesn't expose these columns
      // and locked decision is no backend changes. Empty array if no IDs.
      let projectionsById = new Map<string, ProjectionRow>();
      if (raw.length > 0) {
        const ids = raw.map(p => p.id);
        const { data: projRows } = await (supabase as any)
          .from('nfl_players')
          .select('id, projected_trash_points, projected_anti_points, projected_total_points, adp, status')
          .in('id', ids);
        for (const row of ((projRows as ProjectionRow[] | null) ?? [])) {
          projectionsById.set(row.id, row);
        }
      }

      // Client-side ownership filter — RPC doesn't expose an
      // SAB-993: the ownership filter moved into the RPC (p_availability). It used to run
      // HERE, on the 50 rows the RPC had already paginated -- which meant the default
      // "Available" trimmed each page after the fact: pages came back short and uneven, the
      // header total counted rows the list did not show, and infinite scroll appended
      // partial pages. "Cheap because RPC already paginates" was the bug, not the saving.
      const filtered = raw;

      const mapped: NFLPlayerCardData[] = filtered.map(r => {
        const proj = projectionsById.get(r.id);
        return {
          ...r,
          projected_trash_points: proj?.projected_trash_points ?? null,
          projected_anti_points:  proj?.projected_anti_points  ?? null,
          projected_total_points: proj?.projected_total_points ?? null,
          // SAB-1176: named explicitly rather than relying on the spread. The RPC row is
          // untyped (SAB-1177), so a spread satisfies nothing at compile time, and Postgres
          // numerics arrive as strings over JSON -- Number() is the coercion, not decoration.
          total_yds: Number(r.total_yds ?? 0),
          total_td:  Number(r.total_td ?? 0),
          total_int: Number(r.total_int ?? 0),
          total_fum: Number(r.total_fum ?? 0),
          total_drp: Number(r.total_drp ?? 0),
          total_tfl: Number(r.total_tfl ?? 0),
          // SAB-811: proj_sab_pts + adp arrive on the RPC row via ...r and are the
          // authoritative board values. adp is read from the RPC rather than the
          // supplementary select so the displayed number and the SORTED number are the
          // same read — they are the same column, but one source avoids them ever drifting.
          proj_sab_pts:           r.proj_sab_pts ?? null,
          adp:                    r.adp ?? proj?.adp ?? null,
          // SAB-749: availability marker, display only. Prefer the supplementary
          // read but never clobber the RPC's value with a null.
          status:                 proj?.status ?? r.status,
        };
      });

      setPlayers(prev => append ? [...prev, ...mapped] : mapped);
      setTotalCount(result?.total ?? 0);
    } catch (err) {
      console.error('[NFLPlayersMobile] fetch failed:', err);
      Sentry.captureException(err, { tags: { component: 'NFLPlayersMobile', op: 'fetchPlayers' } });
      setError(err instanceof Error ? err : new Error(String(err)));
      if (!append) setPlayers([]);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [currentLeague, posFilter, debouncedSearch, sortKey, sortDir, ownershipFilter, flex]);

  // Initial / filter-change fetch
  useEffect(() => { fetchPlayers(0, false); }, [fetchPlayers, loadAttempt]);

  // Still-loading toast at 10s — parity with desktop
  useEffect(() => {
    if (!loading) return;
    const t = setTimeout(() => {
      toast({
        title: 'Still loading…',
        description: 'Fetching players over a slow connection. Hang tight.',
      });
    }, 10000);
    return () => clearTimeout(t);
  }, [loading, loadAttempt, toast]);

  // ─── Add/Drop — hits nfl-add-drop edge fn (mirrors NFLPlayersPage:130-168) ──
  const [pendingActionFor, setPendingActionFor] = useState<{ id: string; type: 'add' | 'drop' } | null>(null);

  // SAB-1024: pre-draft is view-only here too. Browsing and search stay.
  const canAcquire = canAcquirePlayers(currentLeague?.draft_status);

  const handleAdd = useCallback(async (player: NFLPlayerCardData) => {
    // The card hides its Add button below; this is the same belt-and-braces guard the
    // desktop router has, so no other caller can reach an acquisition pre-draft.
    if (!canAcquire) return;
    if (!currentTeam || !currentLeague) {
      toast({ title: 'No active league', variant: 'destructive' });
      return;
    }
    setPendingActionFor({ id: player.id, type: 'add' });
    try {
      const { data: session } = await supabase.auth.getSession();
      const token = session?.session?.access_token;
      if (!token) {
        toast({ title: 'Not signed in', variant: 'destructive' });
        return;
      }
      const res = await fetch(
        `${(supabase as any).supabaseUrl}/functions/v1/nfl-add-drop`,
        {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({ action: 'add', team_id: currentTeam.id, player_id: player.id }),
        },
      );
      const data = await res.json();
      if (data.error) {
        // SAB-1016: a full roster is not an error to report, it is a flow to open. The
        // server tells us which refusal this is; NO_VALID_ROSTER_ARRANGEMENT means "there
        // is no arrangement WITHOUT a drop", which is exactly the overlay's question.
        // Every other refusal (locked game, already rostered, on waivers) is still a toast,
        // because no drop the user picks would change the answer.
        // SAB-1035: one classifier for both surfaces, rather than each reading `data.code`
        // its own way. It handles the cases inline branching gets wrong — a null body is an
        // error not a success, and `clears_in_days: 0` survives (truthiness would discard a
        // player clearing today).
        const outcome = classifyAcquireResponse(data, res.ok);
        if (outcome.kind === 'needs_drop' || outcome.kind === 'needs_bid') {
          setOverlayMode(outcome.kind === 'needs_bid' ? 'bid' : 'add');
          setAddDropTarget({
            id: player.id, full_name: player.full_name, position: player.position,
            nfl_team_abbr: player.nfl_team_abbr ?? null, headshot_url: player.headshot_url ?? null,
            status: player.status ?? null, injury_status: player.injury_status ?? null,
          });
        } else if (outcome.kind === 'error') {
          toast({ title: 'Add failed', description: outcome.message, variant: 'destructive' });
        }
      } else {
        toast({ title: 'Added', description: `${player.full_name} added to your team.` });
        setLoadAttempt(n => n + 1);
      }
    } catch (err) {
      toast({ title: 'Error', description: String(err), variant: 'destructive' });
    } finally {
      setPendingActionFor(null);
    }
  }, [currentTeam, currentLeague, toast]);

  const handleDrop = useCallback(async (player: NFLPlayerCardData) => {
    if (!currentTeam || !currentLeague) return;
    setPendingActionFor({ id: player.id, type: 'drop' });
    try {
      const { data: session } = await supabase.auth.getSession();
      const token = session?.session?.access_token;
      if (!token) return;
      const res = await fetch(
        `${(supabase as any).supabaseUrl}/functions/v1/nfl-add-drop`,
        {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({ action: 'drop', team_id: currentTeam.id, player_id: player.id }),
        },
      );
      const data = await res.json();
      if (data.error) {
        toast({ title: 'Drop failed', description: data.error, variant: 'destructive' });
      } else {
        toast({ title: 'Dropped', description: `${player.full_name} dropped.` });
        setLoadAttempt(n => n + 1);
      }
    } catch (err) {
      toast({ title: 'Error', description: String(err), variant: 'destructive' });
    } finally {
      setPendingActionFor(null);
    }
  }, [currentTeam, currentLeague, toast]);

  // ─── FAAB / waiver mode for the strip ─────────────────────────────────
  const [faabRemaining, setFaabRemaining] = useState(0);
  useEffect(() => {
    if (!currentTeam) return;
    let cancelled = false;
    (async () => {
      const { data } = await (supabase as any)
        .from('nfl_teams')
        .select('faab_budget')
        .eq('id', currentTeam.id)
        .single();
      if (!cancelled) setFaabRemaining(Number((data as any)?.faab_budget) || 0);
    })();
    return () => { cancelled = true; };
  }, [currentTeam, loadAttempt]);

  const waiverMode = (currentLeague as any)?.waiver_type || 'fcfs';

  // ─── Filter chip count for the icon badge ─────────────────────────────
  const filterCount = ownershipFilter !== 'available' ? 1 : 0;

  // ─── Virtualizer ──────────────────────────────────────────────────────
  const tableContainerRef = useRef<HTMLDivElement>(null);
  const virtualizer = useWindowVirtualizer({
    count: players.length,
    estimateSize: () => 76,
    overscan: 8,
    measureElement: (el) => el?.getBoundingClientRect().height ?? 76,
    scrollMargin: tableContainerRef.current?.offsetTop ?? 0,
  });

  // ─── Handlers ────────────────────────────────────────────────────────
  const toggleExpanded = (id: string) => {
    setExpandedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const clearAllChips = () => {
    setPosFilter('All');
    setOwnershipFilter('available');
  };

  const handleLoadMore = () => {
    const nextPage = page + 1;
    setPage(nextPage);
    fetchPlayers(nextPage, true);
  };

  const hasMore = players.length < totalCount;

  if (!currentLeague) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] text-center">
        <UserSearch className="h-10 w-10 text-muted-foreground mb-4" />
        <p className="text-sm text-muted-foreground">Join a league to browse players</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col bg-background min-h-screen pb-16">
      {/* Title row */}
      <div className="px-4 pt-3 pb-1.5 flex items-baseline justify-between">
        <h1 className="text-2xl font-display font-extrabold tracking-tight text-foreground">Players</h1>
        <span className="text-[11px] font-display font-semibold uppercase tracking-wider text-muted-foreground">
          NFL
        </span>
      </div>

      <div className="px-3 pt-2">
        <button
          type="button"
          data-testid="toggle-waiver-results"
          onClick={() => setShowResults(v => !v)}
          className="text-[11px] font-medium text-primary"
        >
          {showResults ? 'Hide waiver results' : 'Waiver results'}
        </button>
      </div>
      {showResults && (
        <div className="px-3 pb-2">
          <NFLWaiverResults leagueId={currentLeague?.id ?? null} />
        </div>
      )}

      {/* SAB-1024: says why the Add buttons are gone. Once, at list level, rather than a
          disabled control on every row. */}
      {!canAcquire && (
        <p data-testid="pre-draft-notice" className="px-3 py-2 text-[11px] text-amber-300">
          {PRE_DRAFT_NOTICE}
        </p>
      )}

      {/* Band 0 — AcquisitionStrip (FAAB pill only, on non-FCFS leagues) */}
      <AcquisitionStrip faabRemaining={faabRemaining} waiverMode={waiverMode} />

      {/* Band 1 — sticky search + filter icon */}
      <div className="sticky top-[env(safe-area-inset-top)] z-20 bg-background px-4 py-1.5 flex gap-2">
        <Input
          placeholder="Search players or teams…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="flex-1 h-10 bg-card border-border"
        />
        <Button
          variant="outline"
          size="icon"
          className={cn(
            'h-10 w-10 relative shrink-0',
            filterCount > 0 && 'border-primary text-primary',
          )}
          onClick={() => setFilterSheetOpen(true)}
          aria-label="Filters"
        >
          <SlidersHorizontal className="w-4 h-4" />
          {filterCount > 0 && (
            <span
              className="absolute -top-1 -right-1 min-w-[18px] h-[18px] rounded-full bg-primary text-primary-foreground text-[10px] font-bold flex items-center justify-center px-1"
              aria-label={`${filterCount} active filter${filterCount === 1 ? '' : 's'}`}
            >
              {filterCount}
            </span>
          )}
        </Button>
      </div>

      {/* Band 2 — sticky position priority */}
      <div className="sticky top-[calc(env(safe-area-inset-top)+52px)] z-[19] bg-background px-4 pt-1 pb-2.5">
        <PositionPriorityRow posFilter={posFilter} onPosFilterChange={setPosFilter} options={positionOptions} />
      </div>

      {/* Band 3 — active filter chips */}
      <ActiveFilterChips
        posFilter={posFilter}
        ownershipFilter={ownershipFilter}
        onResetPos={() => setPosFilter('All')}
        onResetOwnership={() => setOwnershipFilter('available')}
        onClearAll={clearAllChips}
      />

      {/* Band 4 — result count + sort */}
      <div className="px-4 py-2 flex items-center justify-between text-[11px] font-display font-semibold uppercase tracking-wider text-muted-foreground">
        <span>
          {players.length.toLocaleString()}
          {totalCount > players.length && ` of ${totalCount.toLocaleString()}`}
          {' '}player{players.length === 1 ? '' : 's'}
        </span>
        <SortMenu
          sortKey={sortKey}
          sortDir={sortDir}
          onSortKeyChange={(k) => {
            setSortKey(k);
            // SAB-811: ADP is ascending-best — ADP 1 is the first pick — so carrying a
            // previous 'desc' into it would open on the worst players and read as broken.
            // Name is A-Z; everything else is higher-is-better.
            setSortDir(k === 'adp' || k === 'full_name' ? 'asc' : 'desc');
          }}
          onSortDirChange={setSortDir}
        />
      </div>

      {/* Band 5 — list */}
      {loading ? (
        <div className="px-4 space-y-2">
          {Array.from({ length: 8 }).map((_, i) => (
            <Skeleton key={i} className="h-[68px] rounded-xl" />
          ))}
        </div>
      ) : error ? (
        <div className="mx-4 my-3 rounded-lg border border-destructive/30 bg-destructive/5 p-6 text-center space-y-3">
          <p className="text-sm font-semibold text-destructive">Failed to load players.</p>
          <p className="text-xs text-muted-foreground">
            {error.message || 'A network or server error occurred.'}
          </p>
          <Button size="sm" onClick={() => setLoadAttempt(n => n + 1)}>Tap to retry</Button>
        </div>
      ) : players.length === 0 ? (
        <div className="mx-4 my-6 text-sm text-muted-foreground text-center">
          No players found.
        </div>
      ) : (
        <>
          <div
            ref={tableContainerRef}
            className="relative"
            style={{ height: virtualizer.getTotalSize() }}
          >
            {virtualizer.getVirtualItems().map((vi) => {
              const player = players[vi.index];
              if (!player) return null;
              const isMine = !!player.owner_team_name && currentTeam
                ? player.owner_team_name === (currentTeam as any).name
                : false;

              return (
                <div
                  key={player.id}
                  data-index={vi.index}
                  ref={virtualizer.measureElement}
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    transform: `translateY(${vi.start - virtualizer.options.scrollMargin}px)`,
                  }}
                >
                  <div className="px-4 pb-2">
                    <PlayerCardMobile
                      player={player}
                      rank={vi.index + 1}
                      isExpanded={expandedIds.has(player.id)}
                      isOnMyTeam={isMine}
                      isAddPending={pendingActionFor?.id === player.id && pendingActionFor.type === 'add'}
                      isDropPending={pendingActionFor?.id === player.id && pendingActionFor.type === 'drop'}
                      onCardClick={() => setDetailPlayerId(player.id)}
                      onToggleExpanded={() => toggleExpanded(player.id)}
                      onAddClick={handleAdd}
                      canAcquire={canAcquire}
                      onDropClick={handleDrop}
                    />
                  </div>
                </div>
              );
            })}
          </div>
          {hasMore && (
            <div className="px-4 py-3 flex justify-center">
              <Button
                variant="outline"
                size="sm"
                onClick={handleLoadMore}
                disabled={loadingMore}
                className="text-xs"
              >
                {loadingMore ? 'Loading…' : `Load more (${totalCount - players.length} remaining)`}
              </Button>
            </div>
          )}
        </>
      )}

      <FilterSheet
        open={filterSheetOpen}
        onOpenChange={setFilterSheetOpen}
        ownershipFilter={ownershipFilter}
        onOwnershipChange={setOwnershipFilter}
      />

      <NFLPlayerDetailModal
        onTrade={onTradeWith ? (teamId, pid) => onTradeWith(teamId, pid) : undefined}
        playerId={detailPlayerId}
        open={!!detailPlayerId}
        onOpenChange={(open) => { if (!open) setDetailPlayerId(null); }}
        // SAB-1045: the SAME row the list badged, so the chip cannot disagree with it.
        waiver={players.find(pl => pl.id === detailPlayerId) ?? undefined}
        // SAB-1045 follow-up: same handler the card's Add button calls. handleAdd attempts
        // the add and routes on the SERVER's refusal code (classifyAcquireResponse), so the
        // detail button inherits bid mode without deciding anything itself.
        onAcquire={() => {
          const p = players.find(pl => pl.id === detailPlayerId);
          if (p) void handleAdd(p);
        }}
      />

      <NFLAddDropOverlay
        open={!!addDropTarget}
        onOpenChange={(v) => { if (!v) setAddDropTarget(null); }}
        addPlayer={addDropTarget}
        mode={overlayMode}
        faabRemaining={faabRemaining}
        rosterSlots={currentLeague?.settings?.roster_slots}
        teamId={currentTeam?.id ?? null}
        leagueId={currentLeague?.id ?? null}
        seasonYear={currentLeague?.season_year ?? null}
        currentWeek={currentLeague?.current_week ?? null}
        onComplete={(msg) => { toast({ title: 'Roster updated', description: msg }); setLoadAttempt(n => n + 1); }}
      />

    </div>
  );
}
